import { AnalysisItem, AnalysisType, GroupType, projectAnalysis, ProjectAnalysisEvents, UnitType } from "@/types/analysis/ProjectAnalysis";
import { ColorDefinition, DataVisualization } from "@/types/colors";
import { AppSettingType, CadModelCategory, FeatureManagementType } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import BarChart, { BarChartApi } from "@components/Charts/BarChart";
import useAppStore from "@hooks/useAppStore";
import { useBenchmarkOverlays } from "@hooks/useBenchmarkOverlays";
import i18n from "@i18n/i18n";
import parseNumber from "@utils/parseNumber";
import { Emitter, EventType } from "mitt";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { ResultsEvent, ResultsEvents } from "../../ResultsEvents";

export interface ByCategoriesBarChartProps {
  width: number | undefined;
  height: number | undefined;
  emitter: Emitter<Record<EventType, unknown>>;
}

const ByCategoriesBarChart = observer((props: ByCategoriesBarChartProps) => {
  console.log("ByCategoriesBarChart: ", props);
  const appStore = useAppStore();
  const cadModelCategories = useCadModelCategories();
  const overlays = useBenchmarkOverlays(AnalysisType.CadModelCategory);
  const [chartColors, setChartColors] = useState<ColorDefinition[]>(DataVisualization.Colors.MaterialsPalette);
  const [chartData, setChartData] = useState([
    ["", ""],
    ["", 0],
  ]);
  const [chartOptions, setChartOptions] = useState({
    title: i18n.t("barchart.title.header"),
    hAxis: {
      title: projectAnalysis.isGroupTypeModel ? i18n.t("results-tab.title.x-axis.models") : i18n.t("results-tab.title.x-axis.scenarios"),
    },
    vAxis: {
      title: i18n.t("results-tab.title.y-axis.categories"),
    },
  });
  const [hasChartData, setHasChartData] = useState(false);
  const [slantedText, setSlantedText] = useState<boolean>(false);
  const chartRef = useRef<BarChartApi | null>(null);

  useEffect(() => {
    updateChartData();
  }, []);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateChartData);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateChartData);
    };
  }, []);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateChartSelections);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateChartSelections);
    };
  }, []);

  useEffect(() => {
    props.emitter.on(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);

    return () => {
      props.emitter.off(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);
    };
  }, []);
  
  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, updateChartData); // Re-render the chart data with new units

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, updateChartData);
    };
  }, []);

  const handleUpdateSelection = async (event: any) => {
    if (event.key === "geometry-loaded") {
      updateViewers(event.groupKey);
    } else if (event.key !== "bar-chart") {
      updateSelection(event.groupKey);
    }
  };

  const updateChartSelections = async () => {
    const groupKeys = projectAnalysis.getSelections(AnalysisType.CadModelCategory);
    const analysisData = projectAnalysis.getAnalysis(AnalysisType.CadModelCategory);
    if (analysisData) {
      const legends = analysisData.getFilteredItems((item: AnalysisItem) => groupKeys.has(item.groupKey)).map((item: AnalysisItem) => item.name);
      chartRef.current?.setSelection({ legends });
    }
  };

  const updateSelection = (groupKey?: string) => {
    chartRef.current?.setSelection({ legends: groupKey });
  };

  const updateChartData = async () => {
    const xAxisTitle = projectAnalysis.isGroupTypeModel ? i18n.t("results-tab.title.x-axis.models") : i18n.t("results-tab.title.x-axis.scenarios");
    const analysisData = projectAnalysis.getAnalysis(AnalysisType.CadModelCategory);
    if (analysisData) {
      const keys = analysisData.getItemNames();
      const newChartData = [];
      newChartData.push([xAxisTitle, ...keys]);
      analysisData.scenarioAnalyses.forEach((scenarioAnalysis) => {
        scenarioAnalysis.groupData.forEach((groupData) => {
          const convertedValues = Array.from(groupData.items.values()).map((item: AnalysisItem) => {
            let convertedValue = item.co2eTotal;

            // Apply unit conversion if feature is enabled
            if (appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
              convertedValue = projectAnalysis.applyUnitConversion(item.co2eTotal, scenarioAnalysis);
            }

            return parseNumber(
              i18n.t("number", { value: convertedValue, digits: AppSettingType.NumberFormat_EC_Digits }).replace(",", ""),
              i18n.language,
            );
          });

          newChartData.push([
            groupData.name,
            ...convertedValues,
          ]);
        });
      });

      setChartColors(analysisData.colorDefinitions());
      setChartData(newChartData);
      setSlantedText(projectAnalysis.groupType === GroupType.Model);
      setHasChartData(true);

      //Update chart axis titles
      let yAxisTitle = i18n.t("results-tab.title.y-axis.categories");

      if (appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
        switch (projectAnalysis.selectedUnitType) {
          case UnitType.KgCO2ePerM2:
            yAxisTitle = i18n.t("results-tab.title.y-axis.categories-m2");
            break;
          case UnitType.KgCO2ePerM2PerYear:
            yAxisTitle = i18n.t("results-tab.title.y-axis.categories-m2-year");
            break;
        }
      }

      setChartOptions((prev) => ({
        ...prev,
        hAxis: {
          ...prev.hAxis,
          title: xAxisTitle,
        },
        vAxis: {
          ...prev.vAxis,
          title: yAxisTitle,
        },
      }));

    } else {
      const newChartData: any = [
        [xAxisTitle, ""],
        ["", 0],
      ];
      setChartColors(DataVisualization.Colors.MaterialsPalette);
      setChartData(newChartData);
      setHasChartData(false);
    }
  };

  const updateViewers = async (groupKey?: string | undefined): Promise<string | undefined> => {
    if (groupKey) {
      const cadModelCategory = cadModelCategories.data?.find((cmc: CadModelCategory) => cmc.label === groupKey);
      await appStore.lmViewer1?.isolateByModelCategoryId(cadModelCategory?.value);
      await appStore.lmViewer2?.isolateByModelCategoryId(cadModelCategory?.value);
    } else {
      await appStore.lmViewer1?.resetView();
      await appStore.lmViewer2?.resetView();
    }

    return groupKey;
  };

  const handleChartSelection = async (_viewerIndex?: number | undefined, colIndex?: number | undefined, groupKey?: string | undefined) => {
    console.log(`updateViewers: ${colIndex}, ${groupKey}`);

    await updateViewers(groupKey);

    const event: ResultsEvent = {
      key: "bar-chart",
      eventType: ResultsEvents.SELECTION_CHANGED,
      groupKey,
    };

    props.emitter.emit(ResultsEvents.SELECTION_CHANGED, event);
  };

  return (
    <>
      {hasChartData && (
        <BarChart
          ref={chartRef}
          id={"bar-chart"}
          width={props.width}
          height={props.height}
          data={chartData}
          colors={chartColors}
          chartOptions={chartOptions}
          slantedText={slantedText}
          overlays={overlays}
          onSelect={(index, legend) => handleChartSelection(-1, index, legend)}
        />
      )}
    </>
  );
});

export default ByCategoriesBarChart;
